import type { NamespaceCaptureTranslation } from '../../i18n-types';

const da_capture = {
  alertMessages: {
    addLocation: 'Tilføj placering',
    cannotDeleteCondition: 'Kan ikke slette tilstanden',
    cannotDeleteLesion: 'Kan ikke slette læsionen',
    cannotDeleteRash: 'Kan ikke slette hudlidelsen',
    conditionHasImageRegistered:
      'Denne tilstand indeholder information og kan ikke slettes.',
    leave: 'Forlad',
    lesionHasImageRegistered:
      'Denne læsion indeholder information og kan ikke slettes.',
    newClinicalAction: 'Ny klinisk handling?',
    newClinicalActionOrNewImage:
      'Du kan enten foretage en ny klinisk handling eller fortsætte med at tage nye billeder',
    wantToLeave: 'Er du sikker på, du vil forlade siden?',
  },
  blurriness: {
    approvedDefault: 'Dit billede bliver gemt, når du fortsætter.',
    approvedDermoscopy:
      'Dit dermatoskopiske billede bliver gemt, når du fortsætter.',
    approvedTwoImages:
      'To billeder (med/uden flash) bliver gemt, når du fortsætter.',
    blurrinessBad: 'Er billedet sløret?',
    blurrinessGood: 'Godt gået!',
    blurrinessUnchecked: 'Er billedet skarpt?',
    disapprovedDefault: 'Prøv at rense linsen, hold kameraet stille.',
    disapprovedDermoscopy:
      'Zoom ind på forandringen for at se, om billedet er skarpt. Tag det om om nødvendigt, eller fortsæt med dette.',
    disapprovedTwoImages:
      'Tag billedet om eller fortsæt, hvis du mener kvaliteten er tilstrækkelig.',
    error:
      'Vi kunne ikke vurdere billedets kvalitet denne gang, men du kan stadig bruge det, hvis det ser fint ud for dig.',
    uncheckedDefault:
      'Se efter, om billedet er skarpt. Tag det om om nødvendigt, eller fortsæt for at gemme det.',
  },
  buttons: {
    addCondition: 'Tilføj tilstand',
    addDifferentCondition: 'Tilføj anden tilstand',
    addImagesAction: 'Tilføj billeder/handling',
    addLesion: 'Tilføj læsion',
    addLocation: 'Tilføj placering',
    addRash: 'Tilføj hudlidelse',
    addSpecimenID: 'Tilføj materiale-ID manuelt',
    addSSN: 'Tilføj CPR manuelt',
    allLesions: 'Alle læsioner',
    dermoscope: 'Dermoskop',
    done: 'Færdig',
    exitDemoMode: 'Forlad Demotilstand',
    findAddNewPatient: 'Find/tilføj ny patient',
    findOrAddPatient: 'Find eller tilføj patient',
    gotToHomePage: 'Gå til forside',
    patientOverview: 'Patientoverblik',
    recentRegistrations: 'Nyeste registreringer',
    restartTheApp: 'Genstart appen',
    retake: 'Tag om',
  },
  camera: {
    addMoreImages: 'Tilføj flere billeder',
    closeUpImagesTitle: 'Nærbillede{{nr:e}}',
    continue: 'Fortsæt',
    deleteImage: {
      message:
        'Er du sikker på, du vil slette dette billede? Denne handling kan ikke fortrydes',
      title: 'Slet billede?',
    },
    dermoscopyImagesTitle: 'Dermatoskopisk{{nr:e}} billede{{nr:r}}',
    overviewImagesTitle: 'Overbliksbillede{{nr:r}}',
  },
  cameraPermissionScreen: {
    actionText: 'Fortsæt',
    message:
      'Denne funktion kræver adgang til kameraet for at fungere korrekt.',
    title: 'Kamera adgang',
  },
  captureInstructions: {
    overviewImageTitle: 'Tips til gode oversigtsbilleder',
    dermoscopicImageTitle: 'Tips til gode dermatoskopiske billeder',
    closeUpImageTitle: 'Tips til gode nærbilleder',
    positionTheCameraAtCorrectDistance: 'Placér kameraet i korrekt afstand',
    ifCameraTooClose:
      'Hvis kameraet er <>for tæt på<>, kan billedet blive uskarpt og ikke give et godt overblik over huden omkring læsionen.',
    ifCameraTooFar:
      'Hvis kameraet er <>for langt væk<>, kan detaljerne i læsionen blive uskarpe, når man zoomer ind på en stor skærm.',
    ensureTheImageIsSharpAndFocused:
      'Sørg for, at billedet er skarpt og i fokus',
    alwaysTapLesionAndWaitForYellowSquare:
      '<>Tryk altid på læsionen på skærmen<> for at fokusere kameraet dér. Vent til den gule firkant vises.',
    ensureLesionInCenter:
      'Sørg om muligt for, at <>læsionen er i midten af billedet<>, markeret med hvide klammer.',
    zoomInToEnsureSharpness:
      'Efter billedet er taget, <>zoom ind<> for at sikre, at læsionen er skarp, sløring kan være svær at opdage på den lille mobilskærm.',
    stillGettingPoorQualityImages: 'Får du stadig billeder af dårlig kvalitet?',
    ensureEnoughLight: 'Sørg for, at der er <>god belysning i rummet<>.',
    keepCameraStill: 'Husk at <>holde kameraet stille<>, når billedet tages.',
    cleanCameraLens:
      '<>Rens kameralinsen<>, hvis billederne fortsat er uskarpe.',
    attachDermoscopeAndTurnItOn: 'Montér dermatoskopet og tænd det',
    pressDermliteButton:
      'Tryk på den røde knap på siden af dermatoskopet for at tænde.',
    onePressForPolarisedLight:
      'Én tryk = polariseret lys (anbefalet). <>Undgå dobbelttryk medmindre upolariseret lys ønskes.<>',
    pressMelascopeButton:
      'Tryk på den sorte knap på siden af dermatoskopet for at tænde.',
    enablePolarisedLight:
      'Aktivér det polariserede lys for optimal billedkvalitet, hvis dit dermatoskop understøtter dette.',
    useEnoughImmersionFluid: 'Brug rigeligt med immersionsvæske',
    applyImmersionFluidGenerously:
      'Påfør rigeligt med sprit eller olie mellem dermatoskop og hud.',
    immersionFluidImportance:
      'Særligt vigtigt ved <>makeup eller tør/skællende hud<>.',
    alwaysUseImmersionFluidInNonPolarisedMode:
      '<>Brug altid immersionsvæske (sprit eller olie)<> ved upolariseret lys, uden det har billederne ringe eller ingen diagnostisk værdi.',
    restDermoscopeDirectlyOnSkin: 'Placer dermatoskopet <>direkte på huden<>.',
    doNotPressDermoscopeTooHard:
      'Tryk ikke for hårdt, da det kan komprimere de små kar omkring læsionen. Vær også forsigtig ved eleverede læsioner. Tryk altid på læsionen på skærmen for at sikre korrekt fokus.',
    alwaysTapLesion:
      'Tryk altid på læsionen på skærmen for at sikre korrekt fokus.',
    ensureContactWithSkin: 'Sørg for, at dermatoskopet har kontakt med huden.',
    cleanCameraAndDermoscopeLens:
      '<>Rens kamera- og dermatoskoplinse<>, hvis billederne fortsat er uskarpe.',
    holdCameraFarEnough:
      'Hold kameraet langt nok væk til, at få både hudlidelse og omkringliggende rask hud med, så der er fuld kontekst for diagnosen.',
    alwaysTapRashAndWaitForYellowSquare:
      'Tryk altid på hudlidelsen for at fokusere kameraet, vent på den gule firkant for at bekræfte, at det er i fokus.',
    placeRashInCenter:
      'Sørg om muligt for, at <>hudlidelsen er i midten af billedet<>, markeret med hvide klammer.',
    alwaysTapRash:
      'Tryk altid på hudlidelsen på skærmen for at sikre korrekt fokus.',
    includeAsMuchSkinAsPossible: 'Få så meget hud med som muligt',
    includeAsMuchSkinAsPossibleInImage:
      'Få så meget hud med på billedet som muligt, fx begge arme.',
    moveCameraAwayForGoodImage:
      'Flyt kameraet længere væk for at få et godt billede',
    recommendedPolarizedLight: 'Anbefalet: polariseret lys',
    nonPolarizedLight: 'Upolariseret lys',
    enoughImmersionFluid: 'Nok\nimmersionsvæske',
    tooLittleImmersionFluid: 'For lidt\nimmersionsvæske',
    footerText:
      'Hvis du har fulgt alle ovenstående retningslinjer, men stadig får billeder af dårlig kvalitet, kontakt support på +45 44 40 82 45',
  },
  clinicalActions: {
    biopsy: 'Biopsi',
    biopsyActionText:
      '{biopsyTypeName|{Curretage: Curretage, Excision: Excision, Incision: Incision, Punch: Stansebiopsi, Shave: Shave, *: Unspecified}} {text: string}',
    clinicalAction: 'Klinisk handling',
    dismissal: 'Afslutning',
    followUpAssessment: 'Opfølgning',
    newImageRegistration: 'Ny billedregistrering',
    noAction: 'Ingen handling',
    notSpecified: 'Ikke angivet',
    plannedBiopsy: 'Planlagt biopsi',
    referral: 'Henvisning',
    referralActionText:
      '{referralType|{physical: Fysisk henvisning, *: Anmod om televurdering}}',
  },
  consentAlert: {
    clickHere: 'ved at trykke her.',
    consentAllOnSave: 'Giv samtykke til alle og gem',
    description: 'Vi vil gerne bede om dit samtykke til følgende',
    mandatory: 'Obligatorisk',
    save: 'Gem mine valg',
    title: 'Samtykke',
  },
  deleteUser: {
    accountDeletionWithinAMonth:
      'Kontosletning udføres manuelt af Melatech-teamet inden for en måned.',
    and: 'og',
    deleteAccount: 'Slet konto',
    demrloopLearn: 'Dermloop Learn',
    dermloopCapture: 'Dermloop Capture',
    dermloopDesktop: 'Dermloop Desktop',
    ifYouRegret: 'hvis du fortryder',
    ifYouRegretYourAccountDeletion:
      'Hvis du fortryder din kontosletning, kan du logge ind på din konto inden din konto slettes. Du vil blive spurgt, om du ønsker at fortryde kontosletningen.',
    registeredAccountDeletion:
      'Vi har registreret din anmodning om kontosletning. Vi sletter din konto',
    requestDeletion: 'Ønsker du at anmode om sletning af din konto?',
    requestReceived: 'Anmodning modtaget',
    thisWillDeleteIn: 'Bemærk, at dette sletter din konto i',
    withinAMonth: 'Inden for en måned',
  },
  demo: {
    alert: {
      button: {
        accept: 'Fortsæt',
        exit: 'Forlad Demo',
      },
      dataDeletionPolicy:
        'De data, du indtaster, bliver slettet efter 24 timer.',
      demoModeConfirmation: 'Du er i <>Dermloop Demotilstand<>.',
      testPatientDisclaimer:
        'Dettet er en <>TEST<>-patient, og de læsioner og/eller hudlidelsen, du registrerer, bliver ikke vurderet.',
      title: 'Dermloop Demo',
    },
    bannerTitle: 'Dermloop Demo',
    enteringModeAlert: {
      button: {
        accept: 'Fortsæt',
        exit: 'Forlad Demo',
      },
      dataResetPolicy: 'De data, du indtaster, bliver slettet efter 24 timer.',
      entryNotice: 'Bemærk, at du er ved at starte Dermloop Demotilstand.',
      testPatientDisclaimer:
        'Det betyder, at dette er en <>TEST<>-patient, og de registreringer, du foretager, bliver ikke vurderet eller taget i betragtning.',
      title: 'Dermloop Demo',
    },
    exitDemoMode: 'Forlad Demotilstand',
    testPatient: 'Testpatient',
  },
  dermoscope: {
    includeSkin: 'Vis hudlidelsens fordeling',
    includeSurrounding: 'Vigtigt at inkludere omkringliggende hud',
    moveCameraAway: 'Flyt kameraet væk for at få et godt billede',
    liveWarnings: {
      dermoscopeTurnedOff: 'Dermatoskopet er slukket',
      dermoscopeTurnedOffMessageDermlite:
        'Tryk én gang på knappen for at tænde med polariseret lys.',
      dermoscopeTurnedOffMessageMelascope: 'Tænd det for at fortsætte.',
      nonPolarisedLight: 'Polariseret lys ikke registreret',
      nonPolarisedLightMessage:
        'Sørg for, at det polariserede lys er aktiveret for optimal billedkvalitet.',
      readyToCapture: 'Klar til optagelse',
      readyToCaptureMessageDermlite:
        'Dermatoskopet er tændt, og det polariserede lys er aktivt.',
      readyToCaptureMessageMelascope: 'Dermatoskopet er tændt.',
      generalInstruction: 'Påmindelse',
      generalInstructionMessage:
        'Sørg for, at dermatoskopet er tændt, og at det polariserede lys er aktiveret.',
    },
    imageWarnings: {
      dermoscopeNotAttached: 'Dermatoskopet er ikke påsat',
      dermoscopeNotAttachedMessage:
        'Påsæt dermatoskopet og tænd det ved at trykke én gang på den røde knap på siden.',
      dermoscopeTurnedOff: 'Dermatoskopet er slukket',
      dermoscopeTurnedOffMessage:
        'Tænd dermatoskopet ved at trykke én gang på den røde knap på siden.',
      nonPolarisedLight: 'Polariseret lys ikke registreret',
      nonPolarisedLightMessage:
        'Aktivér polariseret lys for at få den bedste billedkvalitet. Sluk dermatoskopet, og tryk én gang på den røde knap for at tænde det polariseret lys.',
    },
  },
  descriptors: {
    allLesions: 'Alle læsioner',
    fetchingPatient: 'Henter seneste patientinformation',
    histopathologicalEvaluation: 'Histopatologisk vurdering',
    lastAccessed: 'Sidst tilgået',
    lastClinicalAction: 'Seneste kliniske handling',
    lastRegistration: 'Seneste registrering',
    lesionDoesntMatch: 'Læsion matcher ikke patient',
    lesionImageBaseline: 'Baseline',
    location: 'Placering',
    medicalRecord: 'Journal',
    newAction: 'Ny handling',
    noLesion: 'Ingen gevækst',
    noPatient: 'Ingen patient',
    patientInfo: 'Patientinfo',
    patientOverview: 'Patientoverblik',
    specimenID: 'Materiale-ID',
    teleEvaluation: 'Televurdering',
    tentativeClinicalDiagnosis: 'Tentativ klinisk diagnose',
    histologicalDiagnosis: 'xxx',
  },
  desktopQrLogin: {
    buttonText: 'QR-kode på PC',
    scannerTitle: 'Scan QR-kode på PC',
    invalidCode: 'Ugyldig QR-kode',
  },
  dictation: {
    smartDictation: 'Smart diktering',
    stop: 'Stop',
    startDictation: 'Start diktering',
    stopDictation: 'Stop diktering',
    cleaningUp: 'Behandler…',
    errorPrefix: 'Dikteringsfejl:',
    formatFailed: 'Det kom ikke helt med - prøv lige igen.',
    micPermissionTitle: 'Lad os tænde din mikrofon',
    micPermissionBody:
      'Smart diktering skal bruge din mikrofon for at virke. Vi gemmer din kladde, før vi åbner Indstillinger, så du kan fortsætte præcis hvor du slap.',
    micPermissionCancel: 'Annuller',
    micPermissionOpenSettings: 'Åbn Indstillinger',
    micPermissionDenied: 'Mikrofonen er stadig slået fra',
    audioSessionFailed: 'Kunne ikke aktivere lydsession',
    recordingStartFailed: 'Kunne ikke starte optagelse',
    recordingUnknownError: 'Ukendt fejl under optagelse',
  },
  errorMessages: {
    anError: 'Der opstod en fejl',
    anErrorHappened: 'Der skete en fejl',
    cannotCreateLesion: 'Kan ikke oprette læsion',
    caseUnavailable:
      'Der opstod en fejl, eller casen du forsøger at åbne findes ikke længere.',
    cannotCreateRash: 'Cannot create rash for non persisted patient',
    cannotDeleteLesion: 'Kan ikke slette læsion, der har ventende opgaver.',
    cannotDeletePatientInProgress: 'Kan ikke slette patient, der er undervejs',
    creatingDetails: 'Problem under registrering af {type}sdetaljer',
    creatingHealthRecordConsent: 'Fejl under oprettelse af patientsamtykker',
    createHealthRecord:
      'Kunne ikke oprette patient, tjek dine input og prøv igen',
    creatingLesion: 'Problem under oprettelse af læsionen',
    creatingRash: 'Problem med at oprette hudlidelse',
    creatingTriage: 'Kan ikke oprette registrering',
    error: 'Fejl',
    errorAlternativeImage:
      'Der opstod en fejl under upload af alternativt billede',
    errorOccurredWhileUpdating: 'Der opstod en fejl under opdatering',
    errorPrimaryImage: 'Der opstod en fejl under upload af primært billede',
    errorWhileDeletingImage: 'Der opstod en fejl under sletning af billede',
    errorWhileGettingImage:
      'Der opstod en fejl under hentning af {imagetype} billede',
    errorWhileUploading:
      'Der opstod en fejl under upload af {imagetype} billede',
    gettingHealthRecord: 'Fejl under hentning af patient',
    gettingUser: 'Der opstod en fejl under hentning af bruger',
    healthRecords: 'Der opstod en fejl for patienter',
    inactiveLesion: 'Forsøgte at opdatere inakiv læsion.',
    missingConditionType: 'Det ser ud til, du mangler en tilstandstype',
    newHealthRecord: 'Der opstod en fejl under oprettelse af ny patient',
    newPatient: 'Der opstod en fejl under oprettelse af ny patient',
    updatingPatient: 'Der opstod en fejl under opdateringen af patienten',
    nonmatchigPatient:
      'Kunne ikke opdatere registrering på ikke-matchende patient',
    someError: 'Der opstod en fejl',
    somethingWrong: 'Noget gik galt',
    unauthorized: 'Uautoriseret',
    updatingDetails: 'Problem under opdatering af {type}sdetaljer',
    updatingLesion: 'Der opstod en fejl under opdateringen af læsionen',
    updatingRash: 'Der opstod en fejl under opdatering af hudlidelse',
    patientIsDead: 'CPR nummeret du indtastede er fra en død person',
  },
  caseEditor: {
    editCase: 'Redigér sag',
    save: 'Gem',
    saveAndSendTeleReferral: 'Gem og send telehenvisning',
    saveAndRecordActions: 'Gem og registrér {count} {{handling|handlinger}}',
    clinicalActions: 'Kliniske handlinger',
    undo: 'Fortryd',
    cancelReferral: 'Annullér henvisning',
    keepReferral: 'Behold henvisning',
    cancelReferralPrompt: {
      title: 'Annullér henvisningen?',
      message: 'Henvisningen annulleres, når du gemmer sagen.',
    },
    status: {
      awaitingAnswer: 'Afventer svar',
      answered: 'Besvaret',
      cancelled: 'Annulleret',
      closed: 'Lukket',
      recorded: 'Registreret',
    },
    saveAndCancelTeleReferral: 'Gem og annullér telehenvisning',
    saveAndRemoveActions: 'Gem og fjern {count} {{handling|handlinger}}',
    saveAndApplyChanges: 'Gem og anvend {count} {{ændring|ændringer}}',
    removeAction: 'Fjern {action}',
    removedOnSave: 'Fjernes, når du gemmer',
    cancelledOnSave: 'Annulleres, når du gemmer',
    sentOn: 'Sendt {date}',
    sentOnBy: 'Sendt {date} af {actor}',
    followUpMonths: '{count} {{måned|måneder}}',
    newClinicalAction: 'Ny klinisk handling',
    noNewAction: 'Ingen ny handling',
    anotherAction: 'Endnu en {action}',
    actionInstance: '{action} {number}',
    limitReason: {
      teleReferralAwaitingAnswer:
        'En telehenvisning afventer allerede svar. Specialisten ser disse ændringer.',
      alreadyConcluded: 'Sagen er allerede afsluttet.',
    },
    discardChanges: 'Kassér ændringer',
    discardPrompt: {
      title: 'Kassér ændringer?',
      message: 'Dine ændringer af sagen er ikke gemt og vil gå tabt.',
    },
    missingImages: {
      title: 'Der mangler påkrævede billeder',
      message:
        'Nogle påkrævede billeder er ikke taget endnu. Tag dem nu, eller gem kladden og tilføj dem senere.',
      saveDraftAnyway: 'Gem kladde alligevel',
    },
    staleEdit: {
      title: 'Sagen er blevet ændret',
      message:
        'En nyere version af sagen blev gemt, efter du begyndte at redigere, så dine ændringer kunne ikke gemmes. De vises stadig på skærmen. Genindlæs sagen for at se de seneste oplysninger.',
      reload: 'Genindlæs',
    },
  },
  formRenderer: {
    photoRemoval: {
      deleteTitle: 'Slet dette billede permanent?',
      deleteMessage:
        'Det fjernes fra sagen, også fra dens historik. Det kan ikke fortrydes.',
      deleteRefused:
        'Billedet kan ikke længere slettes, så det bliver i sagen.',
    },
    addAnotherImage: 'Tilføj endnu et billede',
    addImages: 'Tilføj billeder',
    cameraPermissionDenied:
      'Kameraadgang er nødvendig for at tage billeder. Aktivér den i Indstillinger.',
    cameraTip: 'Sørg for god belysning og skarp fokus',
    cervixCameraTip: 'Gå tæt på, og hold stille under optagelsen',
    cameraUnavailable: 'Der er ikke noget kamera tilgængeligt på denne enhed.',
    captureFailed: 'Billedet kunne ikke tages. Tryk for at prøve igen.',
    uploadFailed: 'Upload mislykkedes',
    completeRegistration: 'Fuldfør indtastning',
    conclude: 'Afslut',
    continueToNextImage: 'Fortsæt til næste billede',
    continueToNextLocation: 'Fortsæt til næste placering',
    deleteImage: 'Slet billede',
    guideExamples: 'Eksempler på et godt og et dårligt billede',
    help: {
      positionThePatient: 'Positionér patienten',
      tips: {
        avoidObstruction:
          'Sørg for, at fingre eller andre genstande ikke blokerer billedet.',
        checkSharpnessAfterCapture:
          'Zoom ind efter optagelsen for at tjekke, at billedet er skarpt — sløring kan være svær at se på en lille skærm.',
        ensureGoodLighting:
          'Sørg for tilstrækkeligt jævnt lys, og undgå kraftige skygger.',
        fillTheFrame: 'Gå tæt nok på, så området fylder det meste af billedet.',
        holdSteady:
          'Hold kameraet stille, og vent på at billedet fokuserer, før du tager billedet.',
        mucoBuccalLeftFraming:
          'Bed patienten om at åbne munden og forsigtigt trække den venstre kind til side for at blotlægge indersiden.',
        mucoBuccalRightFraming:
          'Bed patienten om at åbne munden og forsigtigt trække den højre kind til side for at blotlægge indersiden.',
        mucoLesionFraming:
          'Centrer fundet i billedet, og gå tæt nok på til at fange fine detaljer.',
        mucoLipsClosedFraming:
          'Bed patienten om forsigtigt at lukke læberne i en afslappet, naturlig position.',
        mucoMouthFloorFraming:
          'Bed patienten om at holde tungen løftet op mod ganen, så mundbunden nedenunder er helt synlig.',
        mucoOcclusionGumsFraming:
          'Bed patienten om at bide sammen, så både over- og undertænder samt tandkød er synlige.',
        mucoPalateFraming:
          'Bed patienten om at lægge hovedet tilbage og åbne munden bredt, så ganen er tydeligt synlig.',
        mucoTongueDorsumFraming:
          'Bed patienten om at række tungen ud og holde den fladt, så oversiden er helt synlig.',
        mucoTongueLateralLeftFraming:
          'Bed patienten om at dreje tungen for at vise venstre side, evt. med gaze til at holde fat.',
        mucoTongueLateralRightFraming:
          'Bed patienten om at dreje tungen for at vise højre side, evt. med gaze til at holde fat.',
        mucoTongueVentralFraming:
          'Bed patienten om at løfte tungen op mod ganen, så undersiden fylder billedet.',
      },
      titles: {
        generic: 'Tips til at tage et godt billede',
        mucoBuccalLeft: 'Tips til at fotografere indersiden af venstre kind',
        mucoBuccalRight: 'Tips til at fotografere indersiden af højre kind',
        mucoLesion: 'Tips til at fotografere et nærbillede af fundet',
        mucoLipsClosed: 'Tips til at fotografere lukkede læber',
        mucoMouthFloor: 'Tips til at fotografere mundbunden',
        mucoOcclusionGums: 'Tips til at fotografere bid og tandkød',
        mucoPalate: 'Tips til at fotografere ganen',
        mucoTongueDorsum: 'Tips til at fotografere oversiden af tungen',
        mucoTongueLateralLeft: 'Tips til at fotografere venstre side af tungen',
        mucoTongueLateralRight: 'Tips til at fotografere højre side af tungen',
        mucoTongueVentral: 'Tips til at fotografere tungens underside',
      },
    },
    imagePlural: 'billeder',
    imageSingular: 'billede',
    imagesProgress: 'billeder',
    locationHelp: {
      title: 'Sådan markerer du en placering',
      chooseSurface: 'Vælg fladen',
      chooseSurfaceDescription:
        'Vælg den tegning, der viser, hvor fundet er placeret.',
      exploreModel: 'Rotér og zoom modellen',
      exploreModelDescription:
        'Træk for at rotere modellen. Knib for at zoome ind eller ud.',
      placeMarker: 'Placér eller flyt markøren',
      placeMarker2dDescription:
        'Tryk på tegningen for at placere markøren. Tryk igen for at flytte den. Markøren bliver <>grøn<>, når placeringen er valgt.',
      placeMarker3dDescription:
        'Tryk på modellen for at placere markøren. Tryk igen for at flytte den. Markøren bliver <>grøn<>, når placeringen er valgt.',
      save: 'Gem og fortsæt',
      saveDescription:
        'Kontrollér, at den grønne markør er placeret korrekt, og gem eller fortsæt derefter til næste placering.',
    },
    notePlaceholder: 'Angiv flere detaljer her...',
    markerOnThisSurface: 'Markering placeret her',
    noMarkerOnThisSurface: 'Ingen markering her',
    modelUnavailable:
      '3D-modellen er ikke tilgængelig i denne version af appen',
    placeMarkerHint: 'Tryk på illustrationen for at sætte markøren',
    placeMarkerHint3d: 'Tryk på modellen for at sætte markøren',
    placeMarkerTitle: 'Tryk på tegningen for at placere markør',
    placeMarkerTitle3d: 'Tryk på modellen for at placere markør',
    saveImagesToJournal: 'Gem billeder i Dermloop-journalen',
    saveLocationToJournal: 'Gem i Dermloop-journalen',
    selectAllThatApply: 'Vælg alle der gælder',
    sendTeleReferral: 'Send telehenvisning',
    showLocation: 'Vis placering',
    takePhoto: 'Tag billede',
    unsupportedWidget:
      'Denne formular indeholder et spørgsmål, som denne app-version ikke kan vise. Opdater venligst appen.',
  },
  headerElements: {
    recentPatients: 'Seneste patienter',
  },
  images: {
    addImages: 'Tilføj billeder',
    addImagesAndLocation: 'Tilføj billeder og placering',
    additionalImages: 'Yderligere billeder',
    takeImages: 'Tag billeder',
  },
  loadingMessage: {
    creatingNewLesion: 'Tilføjer ny læsion',
    creatingNewRash: 'Tilføjer ny hudlidelse',
    uploadingImages: 'Uploader billeder... Vent venligst.',
    weAreRetrievingPatientInfo:
      'Henter patientinformation, det burde ikke tage lang tid',
  },
  login: {
    title: 'Log ind på enheden',
    subtitle: 'Du finder QR-koden på din computer.',
    scanAndLogIn: 'Scan og log ind',
    openQrScanner: 'Åbn QR-scanner',
    noComputer: 'Arbejder du uden computer?',
    continueWithEmail: 'Fortsæt med e-mail',
    usePersonalPhone: 'Brug din egen telefon til at logge ind',
  },
  pathologyScanner: {
    addButton: 'Tilføj nummer manuelt',
    helperText: 'Scan stregkoden',
  },
  patientScanner: {
    addAddressManually: 'Indtast adresse manuelt',
    addressLabel: 'Adresse',
    addressSectionLabel: 'Indtast patientens adresse og e-mail',
    addressSectionTitle: 'Adresse og email',
    cityLabel: 'By',
    emailLabel: 'Email',
    inputOverviewDiscrepancyMessage:
      'Kontonummeret er allerede tilknyttet en anden patient. Hvis oplysningerne ikke stemmer overens, bedes du kontakte support på <>{supportPhone}<> for hjælp.',
    inputOverviewLoadingTitle: 'Venter på data...',
    inputOverviewTitle: 'Konto Nr. ⦁ {accountNumber}',
    inputOverviewTitleShort: 'Nr. ⦁ {accountNumber}',
    invalidEmail: 'Indtast en gyldig e-mailadresse',
    invalidPostalCode: 'Indtast et gyldigt postnummer',
    phoneNumberLabel: 'Indtast patientens telefonnummer',
    postalCodeLabel: 'Postnummer',
    requiredField: 'Dette felt er påkrævet',
    scanAddress: 'Scan patientens adresse',
  },
  patientCreator: {
    addPatientInfoManually: 'Tilføj patientinfo manuelt',
    addAthenaManually: 'Tilføj patientinfo manuelt',
    addEcwInfoManually: 'Tilføj patientinfo manuelt',
    addEpicInfoManually: 'Tilføj patientinfo manuelt',
    addSsnManually: 'Tilføj CPR-nr. manuelt',
    addTomedoInfoManually: 'Tilføj patientinfo manuelt',
    addQuincyInfoManually: 'Tilføj patientinfo manuelt',
    addMedisoftwareInfoManually: 'Tilføj patientinfo manuelt',
    errorMessage: {
      duplicateMrn: 'Duplikeret journalnr.',
      invalidMrn: 'Ugyldigt journalnr.',
      invalidSsn: 'Ugyldigt CPR',
    },
    labels: {
      birthDate: 'Fødselsdato',
      firstName: 'Fornavn',
      gender: 'Køn',
      lastName: 'Efternavn',
      mrn: 'Account number',
      transgender: 'Transkønnet',
    },
    scanPatientInfo: 'Scan patientinfo',
    scanAthenaInfo: 'Scan patientinfo',
    scanEcwInfo: 'Scan patientinfo',
    scanEpicInfo: 'Scan patientinfo',
    scanSsnOrBarcode: 'Scan CPR eller stregkode',
    scanTomedoInfo: 'Scan patientinfo',
    scanQuincyInfo: 'Scan patientinfo',
    scanMedisoftwareInfo: 'Scan patientinfo',
  },
  plaintext: {
    additionsJoinSameOrder: 'Tilføjelser nedenfor kommer på samme ordre.',
    completedEntry: 'Din registrering er fuldført',
    completedPathology:
      'Din registrering er sendt til histopatologisk vurdering',
    completedSteps: 'Færdiggjorte trin',
    completedTeleReferral:
      'Din registrering er sendt til teledermatologisk vurdering',
    howToContinueFromHere: 'Hvordan vil du fortsætte?',
    lastAccessed: 'Sidst tilgået',
    participationInTeleProject: 'Deltagelse i det teledermatologiske projekt',
    recentRegistrations: 'Dine seneste registreringer vises her',
    saveAsDraft: 'Gem som kladde',
    selectExistingCondition: 'Vælg en eksisterende tilstand',
    selectExistingLesion: 'Vælg en eksisterende læsion',
    selectExistingRash: 'Vælg en eksisterende hudlidelse',
    selectSpecialityToRegister:
      'Vælg det speciale, du ønsker at registrere eller gennemgå inden for',
    selectTypeConditionToRegister:
      'Vælg den type tilstand, du ønsker at registrere eller gennemgå',
    signOrderInEpicToSubmit:
      'Signér ordren i Epic for at sende denne telehenvisning',
    unit: '{unit}',
    youCanEditAndFinalizeLater: 'Du kan redigere og færdiggøre senere',
  },
  scanner: {
    input: {
      confirmPatient: 'Bekræft patient',
      fillingInInfo: 'Udfyld information',
      infoEntered: 'Information udfyldt',
      invalidInfo: 'Ugyldig information',
      patientFound: 'Patient fundet',
    },
  },
  searchResults: {
    startTypingToSeeMoreResults: 'Begynd at skrive for at se flere resultater',
  },
  conditionOverview: {
    footerButton: {
      goToRegistration: 'Tilbage',
      makeNewRegistration: 'Tilføj billeder/handling',
    },
    thumbnailImage: {
      closeUp: 'Nærbillede',
      dermoscopy: 'Dermoskopi',
      overview: 'Overblik',
    },
  },
  ssnScanner: {
    inputOverviewLoadingTitle: 'Venter på data...',
    inputOverviewTitle: 'CPR ⦁ {ssn}',
    overviewValues: {
      birthDate: 'Fødselsdato',
      gender: 'Køn',
      firstName: 'Fornavn',
      lastName: 'Efternavn',
      isNameProtected: '*****',
    },
  },
  secondImageWarning: {
    title: 'Hov - er det samme læsion?',
    message:
      'Flere billeder af <>samme læsion<> er fint - men en <>anden læsion<> skal have sin egen sag.',
    note: 'Færdiggør denne sag, og tilføj den nye læsion fra læsionsoversigten.',
    differentLesion: 'Nej, anden læsion',
    sameLesion: 'Ja, samme læsion',
  },
  settings: {
    account: 'Konto',
    app: 'App',
    change: 'Skift',
    clinic: 'Klinisk enhed',
    email: 'E-mail',
    emailLocked: 'E-mailen kan ikke ændres her. Kontakt support.',
    legal: 'Juridisk',
    licensesIntro:
      'Dermloop Capture er bygget på open source-software. Her er de pakker, appen indeholder, og deres licenser.',
    name: 'Navn',
    openSourceLicenses: 'Open source-licenser',
    packageCount: '{count} pakke{{r}}',
    support: 'Support',
    version: 'Version',
  },
  statuses: {
    diagnosed: 'Diagnosticeret',
    noDiagnosis: 'Ingen diagnose',
    noRegistered: 'Ingen registreret diagnose',
    noRegisteredLesion: 'Ingen registreret læsion',
    pendingDiagnostic: 'Afventer diagnose',
    undiagnosable: 'Udiagnosticérbar',
  },
  statusMessages: {
    noAccesToMedicalUnits: 'Du har ikke adgang til nogen kliniske enheder.',
    noMatchingPatient: 'Ingen matchende patient',
    ressourceNotFound: 'Ressource ikke fundet',
    scanHealthInsuranceCard: 'Scan sundhedskort',
    scanSSNOrBarcode: 'Scan CPR eller stregkode',
    selectLesionLocationBeforeContinuing:
      'Før du fortsætter, anbefaler vi, at du angiver læsionens placering',
    selectRashLocationBeforeContinuing:
      'Før du fortsætter, anbefaler vi, at du angiver hudlidelsens primære placering',
    SSN: 'CPR',
    tapToSelectLesionLocation: 'Tryk for at angive læsionens placering',
    tapToSelectRashLocation:
      'Tryk for at angive hudlidelsens primære placering',
    whenYouCaptureACondition:
      'Når du registrerer en tilstand, bliver den vist her',
    whenYouCaptureALesion: 'Når du registrerer en læsion, bliver den vist her',
    whenYouCaptureARash:
      'Når du registrerer et hudlidelsens vises det her som punkt på listen',
    attachDermatoscopeForDermoscopicImage:
      'Påsæt dermatoskop for at tage dermatoskopisk billede',
    detachDermatoscopeForOverviewImage:
      'Fjern dermatoskop, for at tage overbliksbillede',
    detachDermatoscopeForCloseUpImage:
      'Fjern dermatoskop, for at tage nærbillede',
    detachDermatoscopeToScanPatientInfo:
      'Fjern dermatoskop for at scanne patientinfo',
    detachDermatoscopeToScanAthenaInfo:
      'Fjern dermatoskop for at scanne patientinfo',
    detachDermatoscopeToScanEcwInfo:
      'Fjern dermatoskop for at scanne patientinfo',
    detachDermatoscopeToScanEpicInfo:
      'Fjern dermatoskop for at scanne patientinfo',
    detachDermatoscopeToScanSSN: 'Fjern dermatoskop for at scanne CPR-nr',
    detachDermatoscopeToScanPathologyInfo:
      'Fjern dermatoskop for at scanne stregkoden',
    detachDermatoscopeToScanQRCode: 'Fjern dermatoskop for at scanne QR-koden',
    detachDermatoscopeToScanTomedoInfo:
      'Fjern dermatoskop for at scanne patientinfo',
    detachDermatoscopeToScanQuincyInfo:
      'Fjern dermatoskop for at scanne patientinfo',
    detachDermatoscopeToScanMedisoftwareInfo:
      'Fjern dermatoskop for at scanne patientinfo',
  },
  triageRegistration: {
    header: {
      viewImagesOrRecord: 'Se billeder/journal',
    },
    loadingMessage: {
      deleting: 'Sletter...',
    },
    missingInfoAlert: {
      closeUpImage: {
        button: 'Tilføj nærbillede',
        step1: 'Fjern dermoskopet',
        step2: 'Tag et nærbillede ved at flytte kameraet væk',
        title: 'Nærbillede mangler',
      },
      dermoscopicImage: {
        button: 'Tilføj dermatoskopisk billede',
        step1: 'Fastgør dermatoskopet',
        step2: 'Tag et dermatoskopisk billede af læsionen',
        title: 'Dermatoskopisk billede mangler',
      },
      overviewImageLesion: {
        button: 'Tilføj overbliksbillede',
        step1: 'Fjern dermoskopet',
        step2: 'Tag et overbliksbillede af læsionen og den omkringliggende hud',
        title: 'Overbliksbillede mangler',
      },
      overviewImageRash: {
        button: 'Tilføj overbliksbillede',
        step1: 'Fjern dermoskopet',
        step2:
          'Tag et overbliksbillede og inkluder så meget af huden som muligt',
        title: 'Overbliksbillede mangler',
      },
    },
  },
  immersionFluidGuide: {
    title: 'Husk nok immersionsvæske',
    closeLabel: 'Luk vejledning',
    applyAlcohol: 'Påfør alkohol som immersionsvæske på huden',
    applyAlcoholDesc:
      'Det minimerer genskin og gør de dybere hudstrukturer synlige for en præcis analyse.',
    step1: 'Tør <>dermatoskoplinsen<> af med en alkoholswab',
    step2: 'Rens huden på og omkring <>læsionen<>',
    step3: 'Placér dermatoskopet mens huden <>stadig er våd<>',
    disclaimer:
      'Du ser denne vejledning første gang du registrerer en læsion, eller fordi en dermatolog vurderede billedkvaliteten på din seneste case som utilstrækkelig grundet manglende immersionsvæske.',
    understood: 'Forstået',
  },
  warnings: {
    checkInternetConnection: 'Tjek venligst din internetforbindelse',
    credentialsExpired: 'Dine adgangsoplysninger er udløbet',
    reenterPassworf: 'Genindtast venligst',
    requestNotComplete: 'Vi kunne ikke gennemføre din anmodning',
  },
} satisfies NamespaceCaptureTranslation;

export default da_capture;
